import { useCallback, useEffect, useRef } from 'react';
import { isNative, registerBackButtonHandler } from './capacitor';

export const APP_NAVIGATION_EVENT = 'navigation';

export type AppHistoryMode = 'push' | 'replace';

/**
 * Whether this app session has pushed a history entry yet — i.e. whether back has a real
 * screen to return to. False on a cold launch until the user navigates somewhere. Modal
 * layers push directly (see useBackNavigationLayer) and deliberately don't count: a
 * dismissed overlay is not a destination.
 */
let hasPushedHistory = false;

export const hasNavigationHistory = (): boolean => hasPushedHistory;

export const updateAppHistory = (path: string, mode: AppHistoryMode): void => {
    if (mode === 'push') {
        hasPushedHistory = true;
        window.history.pushState(null, '', path);
        return;
    }

    window.history.replaceState(null, '', path);
};

export const navigateApp = (path: string, mode: AppHistoryMode = 'push'): void => {
    updateAppHistory(path, mode);
    window.dispatchEvent(new Event(APP_NAVIGATION_EVENT));
};

interface BackNavigationLayerOptions {
    enabled: boolean;
    onBack: () => void;
    state?: Record<string, unknown>;
}

/**
 * Module-level counter to suppress popstate events fired by cleanup history.back().
 * When > 0, popstate handlers from other layers ignore the event.
 */
let suppressPopstateCount = 0;
const activeLayerStack: symbol[] = [];

/**
 * How long (ms) to ignore popstate after a programmatic history.back().
 * iOS/Mac Safari can deliver that popstate late — or fire it twice — so a
 * one-shot listener misses it and a sibling layer mistakes it for a real back
 * gesture (New Job / expense modal jumping back to the list). A short window
 * swallows every popstate our own back produces.
 */
const PROGRAMMATIC_BACK_SUPPRESS_MS = 400;

/** True while a popstate comes from our own history.back() (closing a layer), not a user back press. */
export const isProgrammaticBack = (): boolean => suppressPopstateCount > 0;

const removeLayerFromStack = (layerId: symbol) => {
    const index = activeLayerStack.lastIndexOf(layerId);
    if (index >= 0) {
        activeLayerStack.splice(index, 1);
    }
};

export const useBackNavigationLayer = ({ enabled, onBack, state }: BackNavigationLayerOptions) => {
    const onBackRef = useRef(onBack);
    const stateRef = useRef(state);
    const hasPushedRef = useRef(false);
    const isHandlingPopRef = useRef(false);
    const cleanupTimeoutRef = useRef<number | null>(null);
    const layerIdRef = useRef(Symbol('back-navigation-layer'));
    const initialPathnameRef = useRef(window.location.href);

    // Keep refs current without triggering the main effect
    useEffect(() => {
        onBackRef.current = onBack;
    });
    useEffect(() => {
        stateRef.current = state;
    });

    const scheduleOrphanedEntryCleanup = useCallback(() => {
        if (cleanupTimeoutRef.current !== null || !hasPushedRef.current || isHandlingPopRef.current) {
            return;
        }

        const currentPathname = window.location.href;
        if (currentPathname !== initialPathnameRef.current) {
            // We navigated away (e.g. via an app link). Do not pop the new history entry.
            hasPushedRef.current = false;
            removeLayerFromStack(layerIdRef.current);
            return;
        }

        cleanupTimeoutRef.current = window.setTimeout(() => {
            cleanupTimeoutRef.current = null;

            if (!hasPushedRef.current || isHandlingPopRef.current) {
                return;
            }

            // Re-check pathname inside the timeout: between the synchronous check above
            // and this deferred callback, another navigation (e.g. a sidebar menu click)
            // may have pushed a new history entry. If the URL changed, calling
            // history.back() would pop *that* entry instead of the orphaned one.
            if (window.location.href !== initialPathnameRef.current) {
                hasPushedRef.current = false;
                removeLayerFromStack(layerIdRef.current);
                return;
            }

            if (activeLayerStack[activeLayerStack.length - 1] !== layerIdRef.current) {
                removeLayerFromStack(layerIdRef.current);
                return;
            }

            hasPushedRef.current = false;
            removeLayerFromStack(layerIdRef.current);
            suppressPopstateCount++;
            window.history.back();
            window.setTimeout(() => { suppressPopstateCount--; }, PROGRAMMATIC_BACK_SUPPRESS_MS);
        }, 0);
    }, []);

    useEffect(() => {
        if (!enabled) {
            // If we had pushed an entry but enabled was flipped off programmatically,
            // pop the orphaned entry with suppression so sibling layers don't react.
            scheduleOrphanedEntryCleanup();
            return;
        }

        // TODO(ios-back-nav): `backButton` is an Android-only event, so on iOS this registers a
        // callback nothing ever invokes and the web pushState/popstate path below is skipped.
        // Modals are therefore dismissable only via their on-screen close button — which does
        // work, because the callback returned by this hook short-circuits to onBack on native.
        //
        // Do NOT "fix" this by swapping isNative() for isAndroid() on its own. Capacitor never
        // sets allowsBackForwardNavigationGestures and exposes no config for it, so WKWebView's
        // edge-swipe is off and *nothing on iOS would fire a popstate*. That change would add
        // history entries, suppression counters and orphan cleanup on iOS for zero user-visible
        // gain. A real fix is native + JS together: enable the gesture in a
        // CAPBridgeViewController subclass, then take the web path here — and test nested modals
        // plus the history floor, since swiping back past the first entry can navigate the
        // WebView out of the SPA. Swipe-down-to-dismiss on the sheets is the other option.
        if (isNative()) {
            return registerBackButtonHandler(() => {
                onBackRef.current();
            });
        }

        if (cleanupTimeoutRef.current !== null) {
            window.clearTimeout(cleanupTimeoutRef.current);
            cleanupTimeoutRef.current = null;
        }

        initialPathnameRef.current = window.location.href;

        if (!hasPushedRef.current) {
            hasPushedRef.current = true;
            window.history.pushState({ ...(window.history.state ?? {}), ...stateRef.current }, '');
        }

        if (!activeLayerStack.includes(layerIdRef.current)) {
            activeLayerStack.push(layerIdRef.current);
        }

        const handlePopState = () => {
            if (suppressPopstateCount > 0) return;
            if (activeLayerStack[activeLayerStack.length - 1] !== layerIdRef.current) return;

            suppressPopstateCount++;
            queueMicrotask(() => {
                suppressPopstateCount--;
            });

            if (cleanupTimeoutRef.current !== null) {
                window.clearTimeout(cleanupTimeoutRef.current);
                cleanupTimeoutRef.current = null;
            }

            hasPushedRef.current = false;
            removeLayerFromStack(layerIdRef.current);
            isHandlingPopRef.current = true;
            onBackRef.current();
            queueMicrotask(() => {
                isHandlingPopRef.current = false;
            });
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
            if (!hasPushedRef.current) {
                removeLayerFromStack(layerIdRef.current);
            }
            // Pop orphaned entry when effect cleans up (e.g. component unmount while enabled)
            scheduleOrphanedEntryCleanup();
        };
    }, [enabled, scheduleOrphanedEntryCleanup]);

    return useCallback(() => {
        if (!enabled) {
            onBackRef.current();
            return;
        }

        if (isNative() || isHandlingPopRef.current) {
            onBackRef.current();
            return;
        }

        // Not the top layer (split screen: a job open on each side, closing the older one). A plain
        // back would close the top layer instead, so drop one history entry quietly and close this
        // one directly. Entries are interchangeable; only their count has to match the open layers.
        if (hasPushedRef.current && activeLayerStack[activeLayerStack.length - 1] !== layerIdRef.current) {
            hasPushedRef.current = false;
            removeLayerFromStack(layerIdRef.current);
            suppressPopstateCount++;
            window.history.back();
            window.setTimeout(() => { suppressPopstateCount--; }, PROGRAMMATIC_BACK_SUPPRESS_MS);
            onBackRef.current();
            return;
        }

        // Trigger history.back() which fires our popstate handler → calls onBack
        window.history.back();
    }, [enabled]);
};